export const CHEAT_TYPES = [
  'FACE_DETECTED',
  'LOOKING_AWAY',
  'NO_FACE_DETECTED',
  'LOOKING_AWAY_ALERT',
  'TAB_SWITCHING',
  'MULTIPLE_FACES_DETECTED',
  'CAMERA_ACCESS_LOST',
  'MIC_ACCESS_LOST',
  'AUDIO_DETECTED',
  'SPEECH_DETECTED',
] as const

export type CheatingEventType = (typeof CHEAT_TYPES)[number]

export interface CheatingEvent {
  id: string
  type: CheatingEventType
  timestamp: string
  offset: number
  verified: boolean
  attempt_number?: number
  metadata?: {
    duration?: number
    faceCount?: number
    start_sec?: number
    end_sec?: number
  }
}

export const CHEAT_TYPE_MAP: Record<CheatingEventType, string> = {
  FACE_DETECTED: 'Yüz Tespit Edildi',
  LOOKING_AWAY: 'Başka Yöne Bakma',
  NO_FACE_DETECTED: 'Yüz Tespit Edilemedi',
  MULTIPLE_FACES_DETECTED: 'Birden Fazla Yüz Tespit Edildi',
  LOOKING_AWAY_ALERT: 'Uzun Süre Başka Yöne Bakma',
  TAB_SWITCHING: 'Sekme Değiştirme',
  CAMERA_ACCESS_LOST: 'Kamera Erişim Hatası',
  MIC_ACCESS_LOST: 'Mikrofon Erişim Hatası',
  AUDIO_DETECTED: 'Ses Tespit Edildi',
  SPEECH_DETECTED: 'Konuşma Tespit Edildi',
}

export function getEventTypeText(type: CheatingEventType | string): string {
  return CHEAT_TYPE_MAP[type as CheatingEventType] ?? String(type)
}

export interface CheatEvent {
  id: string
  type: string
  offset: number
  verified: boolean
}

export interface MergedCheatEvent {
  id: string
  ids: string[]
  type: string
  startOffset: number
  endOffset: number
  count: number
  verified: boolean
}

export function mergeCheatEvents(events: CheatEvent[], mergeThreshold = 5.0): MergedCheatEvent[] {
  if (!events || events.length === 0) return []

  // Sort events by offset to ensure proper order
  const sortedEvents = [...events].sort((a, b) => a.offset - b.offset)

  const mergedEvents: MergedCheatEvent[] = []
  let currentGroup: MergedCheatEvent | null = null

  for (const event of sortedEvents) {
    if (!currentGroup) {
      // Start new group
      currentGroup = {
        id: event.id,
        ids: [event.id], // Initialize with first event ID
        type: event.type,
        startOffset: event.offset,
        endOffset: event.offset,
        verified: event.verified,
        count: 1,
      }
    } else if (
      currentGroup.type === event.type &&
      event.offset - currentGroup.endOffset <= mergeThreshold
    ) {
      // Extend current group
      currentGroup.endOffset = event.offset
      currentGroup.ids.push(event.id)
      currentGroup.count++
    } else {
      // Close current group and start new one
      mergedEvents.push(currentGroup)
      currentGroup = {
        id: event.id,
        ids: [event.id],
        type: event.type,
        startOffset: event.offset,
        endOffset: event.offset,
        verified: event.verified,
        count: 1,
      }
    }
  }

  if (currentGroup) {
    mergedEvents.push(currentGroup)
  }

  // Filter out events with duration shorter than 5 seconds
  return mergedEvents.filter(
    (event) =>
      (event.count === 1 && event.type !== 'LOOKING_AWAY') ||
      event.endOffset - event.startOffset >= 5.0
  )
}

export function getTotalMergedEventCount(events: CheatEvent[], mergeThreshold = 5.0): number {
  return mergeCheatEvents(events, mergeThreshold).length
}
